import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  getAccessToken,
  clearAccessToken,
} from "../../services/api/token";
import { refreshAuthSession } from "../../services/api/authApi";
import { useNotifySocket } from "../../services/ws/useNotifySocket";
import useNoticeSocket from "../../services/ws/useNoticeSocket";

const UserContext = createContext(null);
const MUST_CHANGE_PASSWORD_KEY = "mustChangePassword";
const SKIP_REFRESH_ONCE_KEY = "skipRefreshOnce";
const LOGGED_OUT_KEY = "loggedOut";
const USER_WORK_PLACES_KEY = "userWorkPlaces";

const normalizeWorkPlaces = (workPlaces) => {
  if (!Array.isArray(workPlaces)) return [];

  const names = workPlaces
    .map((workPlace) => {
      if (typeof workPlace === "string") return workPlace;
      return workPlace?.work_place ?? "";
    })
    .filter(Boolean);

  return Array.from(new Set(names));
};

const getStoredWorkPlaces = () => {
  try {
    return normalizeWorkPlaces(
      JSON.parse(sessionStorage.getItem(USER_WORK_PLACES_KEY) || "[]")
    );
  } catch (err) {
    return [];
  }
};

export function UserProvider({ children, loginType: initialLoginType }) {
  const [loading, setLoading] = useState(true);


  const [loginType, setLoginType] = useState(initialLoginType);
  const [userUuid, setUserUuid] = useState(null);
  const [userName, setUserName] = useState(null);
  const [workPlaces, setWorkPlacesState] = useState(getStoredWorkPlaces);
  const [mustChangePassword, setMustChangePasswordState] = useState(
    () => sessionStorage.getItem(MUST_CHANGE_PASSWORD_KEY) === "true"
  );

  const setUserWorkPlaces = useCallback((nextWorkPlaces) => {
    const normalizedWorkPlaces = normalizeWorkPlaces(nextWorkPlaces);
    setWorkPlacesState(normalizedWorkPlaces);

    if (normalizedWorkPlaces.length > 0) {
      sessionStorage.setItem(
        USER_WORK_PLACES_KEY,
        JSON.stringify(normalizedWorkPlaces)
      );
    } else {
      sessionStorage.removeItem(USER_WORK_PLACES_KEY);
    }
  }, []);

  const setMustChangePassword = useCallback((value) => {
    const nextValue = !!value;
    setMustChangePasswordState(nextValue);
    if (nextValue) {
      sessionStorage.setItem(MUST_CHANGE_PASSWORD_KEY, "true");
    } else {
      sessionStorage.removeItem(MUST_CHANGE_PASSWORD_KEY);
    }
  }, []);

  const [alarms, setAlarms] = useState([]);
  const [alarmCount, setAlarmCount] = useState(0);
  const [noticeAlarms, setNoticeAlarms] = useState([]);
  const noticeSequenceRef = React.useRef(0);

  const clearNoticeAlarms = useCallback(() => setNoticeAlarms([]), []);
  const addNoticeAlarm = useCallback((data) => {
    const title = data.title.trim();
    setNoticeAlarms((current) => {
      if (current.some((alarm) => alarm.title === title)) return current;

      noticeSequenceRef.current += 1;
      return [
        ...current,
        {
          id: `notice-${Date.now()}-${noticeSequenceRef.current}`,
          type: "notice",
          title,
          description: "새 공지사항을 확인해주세요.",
          date: "",
          time: "",
          read: false,
        },
      ];
    });
  }, []);
  const markNoticeAsRead = useCallback((title) => {
    setNoticeAlarms((current) => {
      const index = current.findIndex((alarm) => alarm.title === title);
      return index < 0 ? current : current.filter((_, itemIndex) => itemIndex !== index);
    });
  }, []);

  /* =========================
     loginType 변경 시 초기화
  ========================= */
  useEffect(() => {
    setAlarms([]);
    setAlarmCount(0);
    setNoticeAlarms([]);
  }, [loginType]);

  useEffect(() => {
    if (initialLoginType) {
      setLoginType(initialLoginType);
    }
  }, [initialLoginType]);

  /* =========================
     인증 동기화
  ========================= */
  const revalidate = useCallback(async () => {
    if (sessionStorage.getItem(LOGGED_OUT_KEY) === "true") {
      clearAccessToken();
      setUserUuid(null);
      setUserName(null);
      setLoginType(null);
      setUserWorkPlaces([]);
      setMustChangePassword(false);
      setAlarms([]);
      setAlarmCount(0);
      setLoading(false);
      return false;
    }

    setLoading(true);
    try {
      const json = await refreshAuthSession();
      const access = json.access;
      const serverRole = json?.Role || json?.role;
      const serverMustChangePassword = json?.must_change_password;

      if (serverRole) setLoginType(serverRole);
      if (serverRole === "admin") {
        setUserWorkPlaces([]);
      }
      if (typeof serverMustChangePassword === "boolean") {
        setMustChangePassword(serverMustChangePassword);
      }
      if (Array.isArray(json?.work_places)) {
        setUserWorkPlaces(json.work_places);
      }
      
      const payload = JSON.parse(atob(access.split(".")[1]));
      setUserUuid(payload?.sub ?? null);
      setUserName(payload?.user_name ?? payload?.admin_name ?? null);

      return true;
    } catch (err) {
      console.error( err.message);
      clearAccessToken();
      setUserUuid(null);
      setUserName(null);
      setUserWorkPlaces([]);
      setMustChangePassword(false);
      setAlarms([]);
      setAlarmCount(0);
      return false;
    } finally {
      setLoading(false);
    }
  }, [setMustChangePassword, setUserWorkPlaces]);

  useEffect(() => {
    const token = getAccessToken();
    if (sessionStorage.getItem(LOGGED_OUT_KEY) === "true") {
      clearAccessToken();
      setUserUuid(null);
      setUserName(null);
      setLoginType(null);
      setUserWorkPlaces([]);
      setMustChangePassword(false);
      setAlarms([]);
      setAlarmCount(0);
      setLoading(false);
      return;
    }

    if (sessionStorage.getItem(SKIP_REFRESH_ONCE_KEY) === "true") {
      sessionStorage.removeItem(SKIP_REFRESH_ONCE_KEY);
      setLoading(false);
      return;
    }
    if (!token) {
      revalidate();
    } else {
      setLoading(false);
    }
  }, [revalidate, setMustChangePassword, setUserWorkPlaces]);

  const token = getAccessToken();

  /* =========================
     WebSocket 연결
  ========================= */
  const { connected: wsConnected } = useNotifySocket({
    token: !loading && token && userUuid ? token : null,
    uuid: userUuid,
    loginType,
    onMessage: (data) => {

      /* =========================
         ADMIN 전용 처리
      ========================= */
      if (loginType === "admin") {
        if (typeof data?.count === "number") {
          setAlarmCount(data.count);
        }

        if (Array.isArray(data?.counts_by_month)) {
          const monthlyAlarms = data.counts_by_month
            .filter((item) => /^\d{4}-(0[1-9]|1[0-2])$/.test(item?.work_month || ""))
            .map((item) => {
              const [year, month] = item.work_month.split("-");
              const count = Math.max(0, Number(item.count) || 0);

              return {
                id: `admin-approval-${item.work_month}`,
                type: "admin-approval",
                workMonth: item.work_month,
                title: `${year}년 ${Number(month)}월 승인 대기`,
                description: `${count}건의 근무 승인이 대기 중입니다.`,
                date: "",
                time: "",
                read: false,
              };
            });
          setAlarms(monthlyAlarms);
        }

        return;
      }

      /* =========================
         USER 전용 처리
      ========================= */
      if (loginType === "user") {
        if (Array.isArray(data?.rejects)) {


          const mappedRejects = data.rejects.map((item, index) => ({
            id: `reject-${index}`,
            title: `${item.work_date} 근무 반려`,
            description: item.reject_reason,
            date: item.work_date,
            time: "",
            read: false,
          }));

          setAlarms(mappedRejects);
        }

        if (typeof data?.count === "number") {
          setAlarmCount(data.count);
        }

        return;
      }
    },
  });
  const { connected: noticeWsConnected } = useNoticeSocket({
    token: !loading && token && userUuid ? token : null,
    // The notice socket is a user-only endpoint. Enabling it for admins makes
    // the rejected connection enter the hook's reconnect loop indefinitely.
    enabled: loginType === "user",
    onConnect: clearNoticeAlarms,
    onMessage: addNoticeAlarm,
  });
  return (
    <UserContext.Provider
      value={{
        loading,
        userUuid,
        userName,
        workPlaces,
        alarms,
        alarmCount,
        noticeAlarms,
        combinedAlarmCount: alarmCount + noticeAlarms.length,
        wsConnected,
        noticeWsConnected,
        loginType, // Alarm에서 사용
        mustChangePassword,
        setUserName,
        setUserWorkPlaces,
        setLoginType,
        setMustChangePassword,
        revalidate,
        markNoticeAsRead,
        logout: ({ skipRefresh = false } = {}) => {
          sessionStorage.setItem(LOGGED_OUT_KEY, "true");
          if (skipRefresh) {
            sessionStorage.setItem(SKIP_REFRESH_ONCE_KEY, "true");
          }
          clearAccessToken();
          setUserUuid(null);
          setUserName(null);
          setLoginType(null);
          setUserWorkPlaces([]);
          setMustChangePassword(false);
          setAlarms([]);
          setAlarmCount(0);
          setNoticeAlarms([]);
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error("useUser must be used within UserProvider");
  }
  return ctx;
}
